import React, { useRef, useState } from 'react'
import useSize from './useSize'
import { scaleLinear } from 'd3-scale'
import { line, area } from 'd3-shape'
import { config } from './config'
import * as Elements from './elements'
import './styles.scss'

const f = new Intl.NumberFormat('de-CH')

export const Chart: React.FC<EasyChart> = ({ data, x, y, heights=[350,550] }) => {
  const [drawData, setDrawData] = useState(data)
  const yFunction = (d: EasyChartArrayType) => {
    return yAxisScale(d)
  }
  const xFunction = (d: EasyChartArrayType, i: number) => xAxisScale(i)
  const [container, size] = useSize()
  const svg = useRef<SVGSVGElement>(null)

  const width = size.width
  const height = size.isMobile ? heights[0] : heights[1]
  const gutterTop = 10
  const gutterRight = 2
  const gutterLeft = size.isMobile ? 30 : 40
  const gutterBottom = 30
  const plotArea = {
    top: gutterTop,
    right: width - gutterRight,
    left: gutterLeft,
    bottom: height - gutterBottom
  }

  const xDomain = x || [
    0,

    Math.max.apply(
      null,
      data.map(m => {
        if (m.type === 'area') return m.values.length
        return m.values.length
      })
    )
  ]
  const yDomain = y || [
    0,
    Math.ceil(
      Math.max.apply(
        null,
        data.map(m => {
          if (m.type === 'area')
            return Math.max.apply(
              null,
              m.values.map((v: EasyChartArrayType) => Math.max(v[0], v[1]))
            )
          return Math.max.apply(null, m.values)
        })
      ) / config.roundTo
    ) * config.roundTo
  ]

  const xAxisScale = React.useMemo(
    () =>
      scaleLinear()
        .domain(xDomain)
        .range([plotArea.left, plotArea.right]),
    [size]
  )

  const yAxisScale = React.useMemo(
    () =>
      scaleLinear()
        .domain(yDomain)
        .range([plotArea.bottom, plotArea.top]),
    [size]
  )

  const lineFunction = React.useMemo(
    () =>
      line<EasyChartArrayType>()
        .x(xFunction)
        .y(yFunction)
        .defined(function(d) {
          return d !== null
        }),
    [size]
  )
  const areaFunction = React.useMemo(
    () =>
      area<EasyChartArrayType>()
        .x(xFunction)
        .y0(d => yFunction(d[0]))
        .y1(d => yFunction(d[1])),
    [size]
  )

  const EasyChartElement: React.FC<EasyChartElement> = ({ props }) => {
    switch (props.type) {
      case 'curve':
        return (
          <Elements.Curve
            values={props.values}
            line={lineFunction}
            className={props.className || 'ticks'}
            area={areaFunction}
            clipped={props.clipped}
          />
        )
      case 'area':
        return (
          <Elements.FilledArea
            values={props.values}
            className={props.className || 'ticks'}
            area={areaFunction}
            clipped={props.clipped}
          />
        )
      case 'vticks':
        return (
          <Elements.VerticalTicks
            values={props.values}
            x={xAxisScale}
            className={props.className || 'ticks'}
            y={[plotArea.top, plotArea.bottom]}
          />
        )
      case 'hticks':
        return (
          <Elements.HorizontalTicks
            values={props.values}
            y={yAxisScale}
            className={props.className || 'ticks'}
            x={[plotArea.left-gutterLeft, plotArea.right]}
          />
        )
      case 'annotations':
        return (
          <Elements.Annotations
            values={props.values}
            x={xAxisScale}
            y={yAxisScale}
          />
        )
      default:
        return <g></g>
    }
  }

  return (
    <div className='easychart'>
      <div ref={container} style={{ position: 'relative', width: '100%' }}>
        <svg
          ref={svg}
          width={width}
          height={height}
          style={{ backgroundColor: 'transparent' }}
        >
          {drawData &&
            drawData.map((element, i) => {
              return (
                <React.Fragment key={i}>
                  <EasyChartElement props={element} />
                </React.Fragment>
              )
            })}
        </svg>
      </div>
    </div>
  )
}
